import { useEffect, useMemo, useState } from "react";
import { TURN_ERRORS_LIMIT, getTurnErrors, listAllProjects, type AdminProject, type TurnErrorRow } from "../api/admin";
import { IconBack, IconX } from "./admin/icons";
import { relativeTime } from "./admin/memberModel";
import "./admin/members.css";
import "./admin/errors.css";

type Props = { onClose: () => void; onOpenUsers: () => void; onOpenUsage: () => void };

/**
 * How long the turn was seen running.
 *
 * Null is a turn that died before its first checkpoint, so how long it ran is
 * unknown rather than zero. Rendering that as 0.0m would read as "returned
 * instantly", the opposite of what those rows mean.
 *
 * Minutes because that is the scale the failure classes sit at: the observed
 * cliffs are minutes long, and a millisecond count would have to be divided by
 * eye on every row.
 */
function ranFor(durationMs: number | null): string {
  return durationMs === null ? "Unknown" : `${(durationMs / 60_000).toFixed(1)}m`;
}

/**
 * The turn's own status in words, falling back to the raw value.
 *
 * A status this page has no word for renders as itself rather than as blank or
 * as an assumed failure, so a status added to the daemon later is visible here
 * without an edit.
 */
function statusLabel(status: string): string {
  if (status === "error") return "Failed";
  if (status === "interrupted") return "Interrupted";
  if (status === "complete") return "Completed";
  return status;
}

function absolute(createdAt: number): string {
  return new Date(createdAt).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminErrorsPage({ onClose, onOpenUsers, onOpenUsage }: Props) {
  const [rows, setRows] = useState<TurnErrorRow[] | null>(null);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setError(null);
    getTurnErrors().then(setRows).catch((e) => setError((e as Error).message));
    // The registry only supplies names for ids this page already has, so a
    // registry that will not load costs the column its names and nothing else.
    // Reporting it as a failure to read turn errors would name the wrong thing.
    listAllProjects().then(setProjects).catch(() => setProjects([]));
  };

  useEffect(() => { refresh(); }, []);

  // The endpoint sends `conversations.project`, which holds an id. The sibling
  // admin tabs show names, so the id is resolved against the registry and falls
  // back to itself for a project that has since been retired or renamed.
  const projectName = useMemo(() => {
    const names = new Map(projects.map((project) => [project.id, project.name]));
    return (id: string) => names.get(id) ?? id;
  }, [projects]);

  // The fetcher asks for exactly this many, and the server sends no total. A
  // full page is therefore the newest of an unknown number rather than all of
  // them, and the count line has to say so instead of presenting the cap as the
  // population.
  const truncated = rows !== null && rows.length >= TURN_ERRORS_LIMIT;

  return (
    <div className="members members-screen errors-screen">
      <div className="members-head">
        <div className="members-head-inner">
          <div className="members-title-row">
            <div className="members-title">
              <h2>Turn errors</h2>
              <p>Chat turns that failed or recorded a reason, newest first.</p>
            </div>
            <div className="members-head-actions">
              <button className="btn btn-ghost" onClick={onClose}><IconBack /> Back to chat</button>
            </div>
          </div>
          <div className="admin-tabs" role="tablist" aria-label="Workspace administration">
            <button role="tab" aria-selected="false" onClick={onOpenUsers}>Users</button>
            <button role="tab" aria-selected="false" onClick={onOpenUsage}>Usage</button>
            <button role="tab" aria-selected="true" className="on">Errors</button>
          </div>
        </div>
      </div>

      <div className="members-body">
        <div className="members-body-inner">
          {error && (
            <div className="members-alert" role="alert">
              <span>Could not load turn errors: {error}</span>
              {/* Dismissing is offered only when there is a table behind the
                  alert to go back to. With nothing loaded, clearing the alert
                  would leave the page on its loading line with no Retry left
                  anywhere, so Retry is deliberately the only way out.
                  As written, nothing reaches the other side of this guard: the
                  only refresh after mount is Retry, which is inside the alert,
                  so an error always means the first load failed and `rows` is
                  still null. The guard is the condition that has to hold if a
                  refresh control or a poll is ever added, not decoration. */}
              {rows !== null && <button onClick={() => setError(null)} aria-label="Dismiss"><IconX /></button>}
              <button className="errors-retry" onClick={refresh}>Retry</button>
            </div>
          )}

          {!rows ? (
            <div className="errors-loading" aria-live="polite">Reading failed turns…</div>
          ) : rows.length === 0 ? (
            <div className="members-empty">No turn has failed or recorded a reason.</div>
          ) : (
            <>
              {/* The server's predicate, restated. Counting these as failures
                  would count an empty completion, which the daemon recorded as a
                  success, among them. */}
              <p className="errors-count">
                {truncated
                  ? `Showing the ${TURN_ERRORS_LIMIT} newest turns that failed or recorded a reason; older ones are not shown.`
                  : `Showing ${rows.length} turn${rows.length === 1 ? "" : "s"} that failed or recorded a reason.`}
              </p>
              <div className="errors-table-wrap">
                <table className="errors-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Account</th>
                      <th>Project</th>
                      <th>Code</th>
                      <th>Ran for</th>
                      <th>Tokens</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {relativeTime(new Date(row.createdAt).toISOString())}
                          <small>{absolute(row.createdAt)}</small>
                        </td>
                        <td className="errors-account">{row.user}</td>
                        <td>{projectName(row.project)}</td>
                        <td>
                          {/* Both the wording and the muted style read the same
                              truthiness test. Mixing truthiness with `??` here
                              would style an empty code as missing and then
                              render the empty string as the label. */}
                          <span className={`errors-code${row.code ? "" : " none"}`}>{row.code || "Not recorded"}</span>
                          <small>{statusLabel(row.status)}</small>
                        </td>
                        <td className="errors-ran">{ranFor(row.durationMs)}</td>
                        {/* `== null` covers undefined too: this row type is
                            hand-mirrored from the server, and a field that drifts
                            out of the copy arrives undefined rather than null. */}
                        <td className="errors-tokens">{row.tokens == null ? "—" : row.tokens.toLocaleString()}</td>
                        {/* error_text is bounded to 2000 characters where it is
                            written. The span is what CSS wraps and clamps, since
                            a clamp on the cell itself would take it out of the
                            table layout; the whole reason stays in the title. */}
                        <td className="errors-reason"><span title={row.text ?? undefined}>{row.text ?? "—"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
